import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Loader2, Music4, AudioWaveform } from "lucide-react";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="sw-app-bg flex min-h-screen flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md sw-fade-up">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 text-[#0B0E14]">
            <AudioWaveform size={26} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-white">SONGWISE AI</h1>
            <p className="font-mono-sw text-[10px] uppercase tracking-[0.22em] text-cyan-400">
              Listen · Separate · Understand · Learn
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-[#222A38] bg-[#131822] p-7 shadow-2xl">
          <h2 className="font-display text-xl font-bold text-white">{title}</h2>
          <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#222A38] bg-[#0B0E14] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20";
const btnCls =
  "flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 text-sm font-semibold text-[#0B0E14] transition-colors hover:bg-cyan-400 disabled:opacity-60";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your studio">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
        <input data-testid="login-email" type="email" required placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password}
          onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <div className="flex justify-end">
          <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-cyan-400 hover:text-cyan-300">
            Forgot password?
          </Link>
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading} className={btnCls}>
          {loading && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-400">
        New here?{" "}
        <Link to="/register" data-testid="go-register" className="font-semibold text-cyan-400 hover:text-cyan-300">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start analyzing and learning your songs">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div data-testid="register-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
        <input data-testid="register-name" required placeholder="Full name" value={name}
          onChange={(e) => setName(e.target.value)} className={inputCls} />
        <input data-testid="register-email" type="email" required placeholder="Email" value={email}
          onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="register-password" type="password" required placeholder="Password (min 6 chars)" value={password}
          onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button data-testid="register-submit" type="submit" disabled={loading} className={btnCls}>
          {loading && <Loader2 size={16} className="animate-spin" />} Create account
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-400">
        Already have an account?{" "}
        <Link to="/login" data-testid="go-login" className="font-semibold text-cyan-400 hover:text-cyan-300">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset your password" subtitle="We'll send you a reset link">
      {sent ? (
        <div data-testid="forgot-sent" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-300">
          If an account exists for {email}, a reset link has been generated. (Demo: check backend logs for the link.)
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}
          <input data-testid="forgot-email" type="email" required placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <button data-testid="forgot-submit" type="submit" disabled={loading} className={btnCls}>
            {loading && <Loader2 size={16} className="animate-spin" />} Send reset link
          </button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-slate-400">
        <Link to="/login" className="font-semibold text-cyan-400 hover:text-cyan-300">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password">
      {done ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-300">
          Password updated. Redirecting to sign in...
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}
          <input data-testid="reset-password" type="password" required placeholder="New password" value={password}
            onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          <button data-testid="reset-submit" type="submit" disabled={loading || !token} className={btnCls}>
            {loading && <Loader2 size={16} className="animate-spin" />} Update password
          </button>
          {!token && <p className="text-xs text-amber-400">Missing reset token in URL.</p>}
        </form>
      )}
    </AuthShell>
  );
}
